import { Controller } from "@hotwired/stimulus"

// Controlador para polling de respuestas de Rankito
// Reemplaza Action Cable con un sistema de polling más simple y confiable
export default class extends Controller {
  static values = {
    respuestaId: Number,
    variant: String
  }

  // Límite de reintentos (100 attempts = ~5 minutos at 3s intervals)
  static MAX_RETRIES = 100
  static POLLING_INTERVAL = 3000 // 3 segundos

  connect() {
    this.retryCount = 0

    // Solo arrancar polling si tenemos un ID de respuesta
    if (this.respuestaIdValue) {
      this.iniciarPolling()
    }
  }

  disconnect() {
    this.detenerPolling()
  }

  iniciarPolling() {
    // Mostrar indicador de que Rankito está trabajando
    this.mostrarIndicadorEspera()

    // Configurar polling usando constante
    this.pollingInterval = setInterval(() => {
      this.verificarRespuesta()
    }, this.constructor.POLLING_INTERVAL)

    // También verificar inmediatamente
    this.verificarRespuesta()
  }

  detenerPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  async verificarRespuesta() {
    // Verificar límite de reintentos
    if (this.retryCount >= this.constructor.MAX_RETRIES) {
      this.mostrarMensajeTimeout('Rankito no pudo responder en el tiempo esperado. Puedes intentar refrescar la página o mencionar a @rankito nuevamente.')
      this.detenerPolling()
      this.ocultarIndicadorEspera()
      this.limpiarParametrosDeUrl()
      return
    }

    this.retryCount++

    try {
      const url = `/api/rankito/poll/${this.respuestaIdValue}`
      const params = new URLSearchParams()

      if (this.variantValue) {
        params.append('variant', this.variantValue)
      }

      const response = await fetch(`${url}?${params}`)
      const data = await response.json()

      if (data.status === 'ready') {
        this.mostrarRespuesta(data)
        this.detenerPolling()
        this.ocultarIndicadorEspera()
        this.limpiarParametrosDeUrl()
      } else if (data.status === 'timeout') {
        this.mostrarMensajeTimeout(data.message)
        this.detenerPolling()
        this.ocultarIndicadorEspera()
        this.limpiarParametrosDeUrl()
      } else if (data.status === 'error') {
        this.detenerPolling()
        this.ocultarIndicadorEspera()
        this.limpiarParametrosDeUrl()
      }
      // Si status === 'pending', continuar polling
    } catch (error) {
      // Log errors en development para debugging
      if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        console.error('RankitoPolling error:', error)
      }
      // No detener polling en caso de error de red temporal
    }
  }

  mostrarRespuesta(data) {
    // Buscar dónde insertar la nueva respuesta
    const parentElement = document.querySelector(`#respuesta_${data.parent_id}`)

    if (parentElement) {
      // Insertar después del elemento padre
      parentElement.insertAdjacentHTML('afterend', data.html)

      // Scroll suave hacia la nueva respuesta
      const nuevaRespuesta = document.querySelector(`#respuesta_${data.respuesta_id}`)
      if (nuevaRespuesta) {
        nuevaRespuesta.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

        // Efecto visual temporal
        nuevaRespuesta.classList.add('nueva-respuesta-rankito')
        setTimeout(() => {
          nuevaRespuesta.classList.remove('nueva-respuesta-rankito')
        }, 3000)
      }
    }
  }

  mostrarIndicadorEspera() {
    // Evitar duplicados
    if (document.getElementById('rankito-thinking-indicator')) {
      return
    }

    // Determinar el mensaje basado en las preferencias de notificación del usuario
    const container = document.querySelector('.core-ThreadPostsList')
    const tieneNotificacionesRespuestas = container?.dataset.tieneNotificacionesRespuestas === 'true'

    let mensaje = 'Rankito está pensando tu respuesta…\nSuele tardar entre 1 y 3 minutos.'

    if (tieneNotificacionesRespuestas) {
      mensaje += ' Te notificaremos cuando Rankito tenga tu respuesta.'
    } else {
      mensaje += ' Si lo prefieres, puedes volver a este hilo pasado ese tiempo para ver tu respuesta. <a href="/preferencias/notificaciones" class="rnk-Link">Activa las notificaciones del foro.</a>'
    }

    // Crear indicador de que Rankito está pensando
    const indicador = document.createElement('div')
    indicador.id = 'rankito-thinking-indicator'
    indicador.className = 'rankito-thinking-indicator'
    indicador.innerHTML = `
      <div style="display: flex; align-items: flex-start; justify-content: center; gap: 0.5rem;">
        <div class="rnk-spinner" style="margin-top: 0.2rem; flex-shrink: 0;"></div>
        <span class="rankito-thinking-text" style="white-space: pre-line; text-align: left;">${mensaje}</span>
      </div>
    `

    // Buscar la última respuesta visible para insertar el spinner justo después
    if (container) {
      const ultimaRespuesta = container.querySelector('[id^="respuesta_"]:last-of-type')

      if (ultimaRespuesta) {
        // Siempre habrá respuestas porque Rankito solo interviene cuando lo mencionan/responden
        ultimaRespuesta.insertAdjacentElement('afterend', indicador)

        // Scroll suave hacia el indicador
        indicador.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      } else {
        // Fallback: añadir al final del container
        container.appendChild(indicador)
      }
    }
  }

  ocultarIndicadorEspera() {
    const indicador = document.getElementById('rankito-thinking-indicator')
    if (indicador) {
      indicador.remove()
    }
  }

  mostrarMensajeTimeout(mensaje) {
    // Reemplazar el spinner con un mensaje de timeout
    const indicador = document.getElementById('rankito-thinking-indicator')
    if (indicador) {
      indicador.innerHTML = `
        <div class="rankito-timeout-message">
          ⏰ ${mensaje}
        </div>
      `
      indicador.className = 'rankito-timeout-indicator'

      // Auto-ocultar después de 10 segundos
      setTimeout(() => {
        this.ocultarIndicadorEspera()
      }, 10000)
    }
  }

  limpiarParametrosDeUrl() {
    const url = new URL(window.location)
    const params = url.searchParams

    if (params.has('respuesta_id')) {
      params.delete('respuesta_id')
      // Actualizar URL sin recargar la página
      window.history.replaceState({}, '', url.toString())
    }
  }
}
